import { DistributionDonut, uploadSegments } from "./DistributionDonut";
import { EXPERIMENT_STATUS, PLOTMAP_STATUS } from "../../constants";
import React, { Fragment, useMemo, useState } from "react";

import PropTypes from "prop-types";
import chroma from "chroma-js";
import { iso31661 } from "iso-3166";

// Bootstrap contextual classes used by EXPERIMENT_STATUS → concrete hex,
// re-tinted to the campaign palette so the donut stays on-brand.
const BOOTSTRAP_HEX = {
  "bg-dark": "#2f3a40",
  "bg-secondary": "#9aa0a6",
  "bg-info": "#3aa0c8",
  "bg-success": "#8fbf48",
  "bg-primary": "#184456",
};

const OTHER_COLOR = "#c3c9cc";
const TOP_N = 7;

const countryName = (alpha) =>
  iso31661.find((c) => c.alpha3 === alpha)?.name ?? alpha;

// Brand-aligned categorical ramp for open-ended dimensions (crops, countries).
const categoricalColors = (count) =>
  chroma
    .scale(["#8fbf48", "#56c596", "#3aa0c8", "#184456", "#f3a64a", "#d98841"])
    .mode("lab")
    .colors(Math.max(count, 1));

// Group sites by a fixed, ordered status enum (plotmap / experiment).
const fromStatus = (sites, field, properties, fallback, hexOf) => {
  const counts = {};
  sites.forEach((site) => {
    const value = site[field] ?? fallback;
    counts[value] = (counts[value] ?? 0) + 1;
  });
  return Object.entries(properties)
    .map(([value, meta]) => ({
      label: meta.label,
      value: counts[value] ?? 0,
      color: hexOf(meta),
      filterValue: value,
    }))
    .filter((segment) => segment.value > 0);
};

// Group sites by a free-text field (crop / country): top-N + an "Other" bucket.
// `valueOf` returns the raw filter value (crop string, country code); `labelOf`
// renders it for display. Segments carry `filterValue` so the legend can drive
// the top filter — except "Unspecified" and "Other", which map to no value.
const fromField = (sites, valueOf, labelOf = (raw) => raw) => {
  const counts = {};
  sites.forEach((site) => {
    const raw = valueOf(site) || "";
    counts[raw] = (counts[raw] ?? 0) + 1;
  });
  const sorted = Object.entries(counts)
    .map(([raw, count]) => ({ raw, count }))
    .sort((a, b) => b.count - a.count);

  const head = sorted.slice(0, TOP_N);
  const tail = sorted.slice(TOP_N);
  const palette = categoricalColors(head.length);
  const segments = head.map(({ raw, count }, index) => ({
    label: raw ? labelOf(raw) : "Unspecified",
    value: count,
    color: palette[index],
    filterValue: raw || null,
  }));
  if (tail.length)
    segments.push({
      label: `Other (${tail.length})`,
      value: tail.reduce((acc, segment) => acc + segment.count, 0),
      color: OTHER_COLOR,
      filterValue: null,
    });

  return segments;
};

// Count distinct, meaningful (non-empty) values of a free-text field. Sites
// with no value don't count — "0 countries" is the honest answer.
const distinctCount = (sites, accessor) => {
  const seen = new Set();
  sites.forEach((site) => {
    const value = accessor(site);
    if (value) seen.add(value);
  });
  return seen.size;
};

// Campaign overview — donut breakdown of the campaign's sites, with a toggle
// to switch the grouping dimension. Colours mirror the status badges.
export const CampaignDistribution = ({
  sites,
  showExperiment,
  uploadTotal,
  uploadsLoading,
  accentColor,
  onFilter,
}) => {
  const dimensions = useMemo(() => {
    const base = [];
    // Upload sessions shown first (loads async; absent until siteUploads lands).
    if (uploadTotal?.total > 0)
      base.push({
        key: "uploads",
        label: "Upload sessions",
        unit: "sessions",
        noun: ["status", "statuses"],
        build: () => uploadSegments(uploadTotal),
      });
    base.push(
      {
        key: "country",
        label: "Countries",
        unit: "sites",
        noun: ["country", "countries"],
        distinct: () => distinctCount(sites, (site) => site.country),
        build: () =>
          fromField(
            sites,
            (site) => site.country,
            (code) => countryName(code)
          ),
      },
      {
        key: "crop",
        label: "Crops",
        unit: "sites",
        noun: ["crop", "crops"],
        distinct: () => distinctCount(sites, (site) => site.crop),
        build: () => fromField(sites, (site) => site.crop),
      },
      {
        key: "plotmap",
        label: "Plot maps",
        unit: "sites",
        noun: ["status", "statuses"],
        build: () =>
          fromStatus(
            sites,
            "plotmap_status",
            PLOTMAP_STATUS.properties,
            PLOTMAP_STATUS.NOT_DELIVERED,
            (meta) => meta.color
          ),
      }
    );
    if (showExperiment)
      base.push({
        key: "experiment",
        label: "Field map",
        unit: "sites",
        noun: ["status", "statuses"],
        build: () =>
          fromStatus(
            sites,
            "experiment_status",
            EXPERIMENT_STATUS.properties,
            EXPERIMENT_STATUS.NOT_REQUIRED,
            (meta) => BOOTSTRAP_HEX[meta.color] ?? OTHER_COLOR
          ),
      });

    return base;
  }, [sites, showExperiment, uploadTotal]);

  // Default tab = first dimension (Upload sessions). We wait for the upload
  // fetch before rendering the donut, so the uploads tab is already present
  // here — no async switch needed. A user pick sets `active` and wins.
  const [active, setActive] = useState(null);
  const current = dimensions.find((dim) => dim.key === active) ?? dimensions[0];

  const segments = useMemo(() => current.build(), [current]);

  const tabs = (
    <div className="cf-dist__toggle" role="tablist" aria-label="Group sites by">
      {dimensions.map((dim, index) => (
        <Fragment key={dim.key}>
          {dimensions.length >= 5 && index === 3 && (
            <span className="cf-dist__tabbreak" aria-hidden="true" />
          )}
          <button
            type="button"
            role="tab"
            aria-selected={dim.key === current.key}
            className={
              "cf-dist__tab" + (dim.key === current.key ? " is-active" : "")
            }
            onClick={() => setActive(dim.key)}
          >
            {dim.label}
          </button>
        </Fragment>
      ))}
    </div>
  );

  return (
    <DistributionDonut
      title="Distribution"
      segments={segments}
      unit={current.unit ?? "sites"}
      accentColor={accentColor}
      tabs={tabs}
      loading={uploadsLoading}
      onSelect={onFilter && ((value) => onFilter(current.key, value))}
    />
  );
};

CampaignDistribution.propTypes = {
  sites: PropTypes.array.isRequired,
  showExperiment: PropTypes.bool,
  uploadTotal: PropTypes.object,
  uploadsLoading: PropTypes.bool,
  accentColor: PropTypes.string,
  onFilter: PropTypes.func,
};
